import type { Locale } from "~/i18n/resolveLocale";

const fallbackOrder: Record<Locale, readonly Locale[]> = {
  en: ["en", "ja"],
  ja: ["ja", "en"],
};

const readString = (label: Record<string, unknown> | null | undefined, key: string): string | null => {
  const v = label?.[key];
  return typeof v === "string" && v.length > 0 ? v : null;
};

// Returns the best available label for the requested locale.
// Falls back to other supported locales if the requested key is missing or empty.
// Returns null only when the label is null/undefined or has no usable value.
export const extractI18nLabel = (label: Record<string, unknown> | null | undefined, locale: Locale): string | null => {
  if (!label) return null;
  for (const candidate of fallbackOrder[locale]) {
    const value = readString(label, candidate);
    if (value !== null) return value;
  }
  return null;
};

export const extractExactI18nLabel = (
  label: Record<string, unknown> | null | undefined,
  locale: Locale,
): string | null => readString(label, locale);

// Display rule for categories: current locale label, then canonical name, then other locale labels.
export const displayCategoryName = (
  category:
    | {
        name?: string | null;
        i18nLabel?: Record<string, unknown> | null;
      }
    | null
    | undefined,
  locale: Locale,
): string => {
  if (!category) return "";

  const localizedLabel = readString(category.i18nLabel, locale);
  if (localizedLabel !== null) return localizedLabel;

  const name = category.name?.trim() ?? "";
  if (name) return name;

  for (const candidate of fallbackOrder[locale]) {
    if (candidate === locale) continue;
    const value = readString(category.i18nLabel, candidate);
    if (value !== null) return value;
  }
  return "";
};

// Display rule for records carrying separate `name` (English) and `nameJa` (Japanese) fields.
// `ja` -> nameJa, then name.  `en` -> name, then nameJa.  Empty strings are skipped.
export const displayName = (
  record: { name?: string | null; nameJa?: string | null } | null | undefined,
  locale: Locale,
): string => {
  if (!record) return "";
  const ja = record.nameJa?.trim() ?? "";
  const en = record.name?.trim() ?? "";
  if (locale === "ja") return ja || en;
  return en || ja;
};
